import { useRef, useState, MouseEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IKnmAttachment, IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { formatBytes } from 'CorePackage/MainModule/common/formatBytes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IconFile } from 'UIPackage/IconsModule/components/IconFile';
import { IKnmWebNotificationInfo } from 'APIPackage/KnmModule/interfaces/IKnmWebNotificationInfo';
import { mapWebNotificationInfoByTitle } from 'KnmErknmPackage/ErknmStatusExchangeModule/constants/mapWebNotificationTypeByTitle';
import { mapWebNotificationStatusByBooleanStatus } from 'KnmErknmPackage/ErknmStatusExchangeModule/constants/mapWebNotificationStatusByBooleanStatus';
import { LoaderWithoutStore } from 'CorePackage/MainModule/components/Loader';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IconCheck } from 'UIPackage/IconsModule/components/IconCheck';
import { IconTime } from 'UIPackage/IconsModule/components/IconTime';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { KnmWebNotificationStatusesEnum } from 'APIPackage/KnmModule/enums/KnmWebNotificationStatusesEnum';
import { KnmWebNotificationTypesEnum } from 'APIPackage/KnmModule/enums/KnmWebNotificationTypesEnum';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';

import { planDownloadDocumentAttachment } from '../actions/planActions';
import { IPlanHistoryItem } from '../interfaces/IPlanHistoryItem';
import styles from './PlanSideInfo.module.scss';
import { PlanHistoryChanges } from './PlanHistoryChanges';

const initialHeight = '0px';
const activeAccordionMarginTop = '30px';

interface IPlanSideInfoItem {
  name: string;
  id?: string;
  data?:
    | {
        type: 'documents';
        docs?: IKnmAttachmentDownload[] | null;
      }
    | {
        type: 'history';
        history?: IPlanHistoryItem[] | null;
      }
    | {
        type: 'web';
        responses?: IKnmWebNotificationInfo[] | null;
      }
    | {
        type: 'versions';
        docs?: IKnmAttachment[] | null;
      }
    | {
        type: 'historyChanges';
        docs?: IKnmHistoryItemChanges[] | undefined;
      };
  isLoading: boolean;
}

export const PlanSideInfoItem = ({ name, data, isLoading, id }: IPlanSideInfoItem) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isActive, setIsActive] = useState(false);
  const [height, setHeight] = useState(initialHeight);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleAccordion = useCallback(() => {
    setIsActive(prev => !prev);
    let newHeight = initialHeight;

    if (!isActive && contentRef && contentRef.current) {
      const current = contentRef.current || {
        scrollHeight: 0,
      };

      newHeight = `${current.scrollHeight}px`;
    }
    setHeight(newHeight);
  }, [isActive]);

  const downloadAttachment = (attachment: IKnmAttachmentDownload) => (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const { fileName, fileSize, filePath, mimeType, bucketName } = attachment;

    dispatch(planDownloadDocumentAttachment({ fileName, filePath, fileSize, mimeType, bucketName }));
  };

  const renderInfoData = () => {
    if (data?.type === 'history') {
      return (
        (data.history?.length &&
          data.history?.map((item, index) => (
            <div className={styles.PlanSideInfoItemInfo} key={index}>
              <div className={styles.PlanSideInfoItemInfoDate}>{item?.date}</div>
              <div className={styles.PlanSideInfoItemInfoName}>{item?.title}</div>
              <div className={styles.PlanSideInfoItemInfoFullName}>{item?.name}</div>
              <IconCheck className={styles.IconCheck} />
            </div>
          ))) || <div className={styles.PlanSideInfoItemInfoEmpty}>Сведения отсутствуют</div>
      );
    }
    if (data?.type === 'documents' || data?.type === 'versions') {
      return (
        (data?.docs?.length &&
          data.docs.map((item, index) => (
            <div className={styles.PlanSideInfoItemDoc} onClick={downloadAttachment(item)} key={index}>
              <div className={styles.PlanSideInfoItemDocWrapper}>
                <IconFile type="side" fileName={item.fileName || ''} />
                <div className={styles.Info}>
                  <span className={styles.FileName}>{item.fileName}</span>
                  <span className={styles.FileSize}>{`${formatBytes(item.fileSize || 0, 0)}`}</span>
                </div>
              </div>
              <div className={styles.UploadIcon} />
            </div>
          ))) || <div className={styles.PlanSideInfoItemInfoEmpty}>Сведения отсутствуют</div>
      );
    }
    if (data?.type === 'web') {
      return (
        (data.responses?.length &&
          data.responses.map((item, index) => {
            const isProgressExclude =
              item.status === KnmWebNotificationStatusesEnum.REGISTERED &&
              item.webNotificationType !== KnmWebNotificationTypesEnum.VP_AGREEMENT;

            return (
              <div className={styles.PlanSideInfoItemInfo} key={index}>
                {item?.created && (
                  <div className={styles.PlanSideInfoItemInfoDate}>
                    {formatDate({
                      date: item?.created,
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: DATE_FORMAT_ISO_FNS,
                    })}
                  </div>
                )}
                <div className={styles.PlanSideInfoItemInfoName}>
                  {item?.webNotificationType && mapWebNotificationInfoByTitle[item.webNotificationType]}
                </div>
                {(item.status && mapWebNotificationStatusByBooleanStatus[item.status]) || isProgressExclude ? (
                  <Tooltip placement="left" content={<div className={styles.TooltipContent}>{item.caseNumber}</div>}>
                    <IconCheck className={styles.IconCheck} />
                  </Tooltip>
                ) : (
                  <Tooltip placement="left" content={<div className={styles.TooltipContent}>В процессе отправки</div>}>
                    <IconTime className={styles.Icon} />
                  </Tooltip>
                )}
              </div>
            );
          })) || <div className={styles.PlanSideInfoItemInfoEmpty}>Сведения отсутствуют</div>
      );
    }
    if (data?.type === 'historyChanges') {
      return data.docs?.length ? (
        <PlanHistoryChanges data={data.docs} />
      ) : (
        <div className={styles.PlanSideInfoItemInfoEmpty}>Сведения отсутствуют</div>
      );
    }

    return null;
  };

  return (
    <div className={styles.PlanSideInfoItem} onClick={toggleAccordion} id={id}>
      <div className={styles.PlanSideInfoItemName}>
        {name}
        <span className={isActive ? styles.PlanSideInfoArrowOpen : styles.PlanSideInfoArrow} />
      </div>

      <div
        className={styles.PlanSideInfoItemAccordion}
        ref={contentRef}
        style={{ maxHeight: `${height}`, marginTop: isActive ? activeAccordionMarginTop : '' }}
      >
        {height !== initialHeight && <LoaderWithoutStore position="absolute" loading={isLoading} />}
        {renderInfoData()}
      </div>
    </div>
  );
};
